/**
 * The supplier's V6 order settings, as they govern what happens to the purchase order when a quote is
 * approved. They apply on every approval route - with or without a supplier technical review - so
 * the decisions live here rather than inside either route.
 */
export interface PurchaseOrderSettings {
  /** The dealer must supply their own order number before the quote can be approved. */
  poNumberRequired: boolean;
  /** The purchase order is issued to the supplier on approval; the dealer has no say. */
  forceIssueOrder: boolean;
}

/**
 * Whether approving needs to stop and ask the dealer. A review always asks (it is where the dealer
 * decides whether the supplier may issue the order). Without a review we ask only when a setting
 * obliges us to - otherwise approval behaves exactly as it did before the settings existed.
 */
export function needsPurchaseOrderPrompt(reviewing: boolean, settings: PurchaseOrderSettings): boolean {
  return reviewing || settings.poNumberRequired || settings.forceIssueOrder;
}

/**
 * Whether the purchase order created on approval goes straight to the supplier. `forceIssueOrder` is
 * an override: it issues the order whoever approves and whatever was answered. Without it, only a
 * supplier agent approving a quote the dealer authorised them to issue does so.
 */
export function shouldIssueOnApproval(
  settings: PurchaseOrderSettings,
  isAgent: boolean,
  supplierAuthorizedToIssue: boolean
): boolean {
  return settings.forceIssueOrder || (isAgent && supplierAuthorizedToIssue);
}
